import { useState, useEffect } from 'react';
import { teamApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { SearchInput } from '../components/ui/SearchInput';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  MoreHorizontal,
  Trash2,
  UsersRound,
  Workflow,
  Brain,
  ChevronLeft,
  User,
  Crown,
  Shield,
  Eye,
} from 'lucide-react';
import { cn } from '../utils/cn';

interface Team {
  id: string;
  name: string;
  ownerId: string;
  ownerName: string;
  ownerEmail: string;
  memberCount: number;
  workflowCount: number;
  aiUsage: number;
  createdAt: string;
  status: string;
}

interface TeamMember {
  id: string;
  userId: string;
  username: string;
  email: string;
  avatar?: string;
  role: string;
  joinedAt: string;
}

interface TeamDetail {
  id: string;
  name: string;
  ownerId: string;
  ownerName: string;
  ownerEmail: string;
  memberCount: number;
  workflowCount: number;
  aiUsage: number;
  createdAt: string;
  status: string;
  members: TeamMember[];
  workflows: Array<{
    id: string;
    name: string;
    nodeCount: number;
    runCount: number;
    createdAt: string;
  }>;
  recentRuns: Array<{
    id: string;
    workflowId: string;
    workflowName: string;
    status: string;
    startedAt: string;
    duration: number;
  }>;
}

export function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamDetail | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'members' | 'workflows' | 'runs'>('members');

  const fetchTeams = async () => {
    setIsLoading(true);
    try {
      const response = await teamApi.listTeams({
        page,
        pageSize,
        keyword: keyword || undefined,
      });
      if (response.code === 0) {
        setTeams(response.data.items);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, [page, pageSize, keyword]);

  const fetchTeamDetail = async (teamId: string) => {
    try {
      const response = await teamApi.getTeamDetail(teamId);
      if (response.code === 0) {
        setSelectedTeam(response.data);
        setShowDetail(true);
      }
    } catch (err) {
      console.error('Failed to fetch team detail:', err);
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (!confirm('确定要删除此团队吗？此操作不可恢复。')) return;
    try {
      await teamApi.deleteTeam(teamId);
      fetchTeams();
    } catch (err) {
      console.error('Failed to delete team:', err);
    }
  };

  const handleUpdateMemberRole = async (teamId: string, memberId: string, role: string) => {
    try {
      await teamApi.updateMemberRole(teamId, memberId, role);
      if (selectedTeam) {
        fetchTeamDetail(selectedTeam.id);
      }
    } catch (err) {
      console.error('Failed to update member role:', err);
    }
  };

  const handleRemoveMember = async (teamId: string, memberId: string) => {
    if (!confirm('确定要移除此成员吗？')) return;
    try {
      await teamApi.removeMember(teamId, memberId);
      if (selectedTeam) {
        fetchTeamDetail(selectedTeam.id);
      }
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('zh-CN');
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-600">
            <Crown className="w-3 h-3" />
            所有者
          </span>
        );
      case 'admin':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
            <Shield className="w-3 h-3" />
            管理员
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600">
            <User className="w-3 h-3" />
            成员
          </span>
        );
    }
  };

  if (showDetail && selectedTeam) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={() => setShowDetail(false)}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900"
        >
          <ChevronLeft className="w-4 h-4" />
          返回团队列表
        </button>

        {/* Team Header */}
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">{selectedTeam.name}</h1>
          <p className="text-zinc-500 mt-1">团队详情与管理</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <UsersRound className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">成员数量</p>
                  <p className="text-xl font-bold text-zinc-900">{selectedTeam.memberCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Workflow className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">工作流</p>
                  <p className="text-xl font-bold text-zinc-900">{selectedTeam.workflowCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Brain className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">AI 使用</p>
                  <p className="text-xl font-bold text-zinc-900">{selectedTeam.aiUsage}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-zinc-100 rounded-lg">
                  <User className="w-5 h-5 text-zinc-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">创建者</p>
                  <p className="text-xl font-bold text-zinc-900 truncate max-w-[120px]">{selectedTeam.ownerName}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="border-b border-zinc-200">
          <nav className="flex gap-6">
            {[
              { key: 'members', label: '团队成员', icon: UsersRound },
              { key: 'workflows', label: '工作流', icon: Workflow },
              { key: 'runs', label: '运行记录', icon: Eye },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={cn(
                  'flex items-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.key
                    ? 'border-accent-600 text-accent-600'
                    : 'border-transparent text-zinc-500 hover:text-zinc-700'
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <Card>
          <CardContent className="p-0">
            {activeTab === 'members' && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>成员</TableHead>
                    <TableHead>角色</TableHead>
                    <TableHead>加入时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedTeam.members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                            {member.avatar ? (
                              <img src={member.avatar} alt={member.username} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <span className="text-accent-700 font-semibold">
                                {member.username.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-zinc-900">{member.username}</p>
                            <p className="text-sm text-zinc-500">{member.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(member.role)}</TableCell>
                      <TableCell>{formatDate(member.joinedAt)}</TableCell>
                      <TableCell className="text-right">
                        {member.role !== 'owner' && (
                          <div className="flex items-center justify-end gap-2">
                            <select
                              value={member.role}
                              onChange={(e) => handleUpdateMemberRole(selectedTeam.id, member.id, e.target.value)}
                              className="text-sm border border-zinc-300 rounded-lg px-2 py-1"
                            >
                              <option value="admin">管理员</option>
                              <option value="member">成员</option>
                              <option value="viewer">查看者</option>
                            </select>
                            <button
                              onClick={() => handleRemoveMember(selectedTeam.id, member.id)}
                              className="p-1.5 text-error-600 hover:bg-error-50 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {activeTab === 'workflows' && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>工作流名称</TableHead>
                    <TableHead>节点数</TableHead>
                    <TableHead>运行次数</TableHead>
                    <TableHead>创建时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedTeam.workflows.map((workflow) => (
                    <TableRow key={workflow.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Workflow className="w-4 h-4 text-zinc-400" />
                          <span className="font-medium text-zinc-900">{workflow.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{workflow.nodeCount}</TableCell>
                      <TableCell>{workflow.runCount}</TableCell>
                      <TableCell>{formatDate(workflow.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                  {selectedTeam.workflows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-zinc-500">
                        暂无工作流
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}

            {activeTab === 'runs' && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>运行 ID</TableHead>
                    <TableHead>工作流</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>执行时间</TableHead>
                    <TableHead>耗时</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedTeam.recentRuns.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell className="font-mono text-sm">{run.id.slice(0, 8)}...</TableCell>
                      <TableCell>{run.workflowName}</TableCell>
                      <TableCell>
                        <Badge variant={run.status === 'success' ? 'success' : run.status === 'failed' ? 'error' : 'warning'}>
                          {run.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(run.startedAt)}</TableCell>
                      <TableCell>{run.duration}ms</TableCell>
                    </TableRow>
                  ))}
                  {selectedTeam.recentRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                        暂无运行记录
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">团队管理</h1>
          <p className="text-zinc-500 mt-1">管理平台所有团队</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchInput
              placeholder="搜索团队名称..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onClear={() => setKeyword('')}
              className="sm:w-80"
            />
          </div>
        </CardContent>
      </Card>

      {/* Teams Table */}
      <Card>
        <CardHeader>
          <CardTitle>团队列表</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>团队名称</TableHead>
                    <TableHead>所有者</TableHead>
                    <TableHead>成员</TableHead>
                    <TableHead>工作流</TableHead>
                    <TableHead>AI 使用</TableHead>
                    <TableHead>创建时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map((team) => (
                    <TableRow key={team.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                            <UsersRound className="w-5 h-5 text-accent-600" />
                          </div>
                          <span className="font-medium text-zinc-900">{team.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-zinc-900">{team.ownerName}</p>
                          <p className="text-sm text-zinc-500">{team.ownerEmail}</p>
                        </div>
                      </TableCell>
                      <TableCell>{team.memberCount}</TableCell>
                      <TableCell>{team.workflowCount}</TableCell>
                      <TableCell>{team.aiUsage}</TableCell>
                      <TableCell>{formatDate(team.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="relative inline-block">
                          <button
                            onClick={() => setShowActionMenu(showActionMenu === team.id ? null : team.id)}
                            className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          
                          {showActionMenu === team.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg border border-zinc-200 shadow-lg z-50">
                              <button
                                onClick={() => {
                                  fetchTeamDetail(team.id);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                              >
                                <Eye className="w-4 h-4" />
                                查看详情
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteTeam(team.id);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-error-600 hover:bg-error-50"
                              >
                                <Trash2 className="w-4 h-4" />
                                删除团队
                              </button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
